import React, { useEffect, useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import { researchosService } from '../../services/researchosService';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';
import { mergeLayout, PLOTLY_CONFIG } from '../../components/charts/chartTheme';

const isDark = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

// Narrative Propagation — the un-investable private-company narrative (OpenAI, Anthropic…) routed
// onto the covered tickers they touch, along the knowledge-map's relationship edges. Deterministic,
// with per-driver provenance. A candidate WATCHLIST for a human — not an auto-trader.

const num = (v) => (typeof v === 'number' && isFinite(v) ? v : null);
const fmt = (v) => { const n = num(v); return n == null ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(3)}`; };
const scoreColor = (v) => {
  const n = num(v);
  if (n == null || Math.abs(n) < 0.001) return 'text-gray-500 dark:text-gray-400';
  return n > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
};

// A tiny time-series sparkline (dependency-free). Green if the trajectory ends up, red if down.
function Spark({ scores }) {
  if (!Array.isArray(scores) || scores.length < 2) return <span className="text-gray-300 dark:text-gray-600">·</span>;
  const w = 116, h = 24, pad = 3;
  const lo = Math.min(...scores), hi = Math.max(...scores), rng = (hi - lo) || 1;
  const pts = scores.map((s, i) => {
    const x = pad + (i / (scores.length - 1)) * (w - 2 * pad);
    const y = h - pad - ((s - lo) / rng) * (h - 2 * pad);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const up = scores[scores.length - 1] >= scores[0];
  const col = up ? '#16a34a' : '#dc2626';
  return (
    <svg width={w} height={h} aria-hidden>
      <polyline points={pts} fill="none" stroke={col} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const RELCLR = { competitor_of: 'text-red-600 dark:text-red-400' };
// Investor-facing driver: lead with WHAT WAS SAID + a link to the source video; the mechanics
// (relation / mention count) are a muted footnote for the curious.
const _latestSourceDate = (d) =>
  (d.sources || []).reduce((m, s) => (s.date && s.date > m ? s.date : m), '');

// Driver-block ordering for an expanded ticker: 'latest' puts the node with the
// freshest video first (what did the newest evidence touch, and its impact);
// 'impact' is the artifact's native |contribution| ranking.
function sortDrivers(drivers, mode) {
  const arr = [...(drivers || [])];
  if (mode === 'latest') {
    arr.sort((a, b) => _latestSourceDate(b).localeCompare(_latestSourceDate(a)));
  } else {
    arr.sort((a, b) => Math.abs(b.contribution || 0) - Math.abs(a.contribution || 0));
  }
  return arr;
}

// ── highlight helpers — mark the entity/ticker that DROVE the score wherever it appears ──
const _esc = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// build a case-insensitive matcher for a set of terms (drops blanks/short noise)
function makeHiRe(terms) {
  const t = (terms || []).map((x) => String(x || '').trim()).filter((x) => x.length >= 2);
  if (!t.length) return null;
  return new RegExp(`(${[...new Set(t)].map(_esc).join('|')})`, 'ig');
}
const hitsAny = (text, re) => !!(re && text && String(text).match(re));
// wrap term matches in <mark>. String.split with a capturing group alternates
// literal/match/literal/… so ODD indices are the captured hits (deterministic — avoids
// the stateful .test()/lastIndex trap of a /g regex reused across calls).
function Highlighted({ text, re }) {
  if (!re || !text) return text || null;
  return String(text).split(re).map((p, i) => (i % 2 === 1
    ? <mark key={i} className="rounded bg-brand-gold/25 px-0.5 text-inherit">{p}</mark>
    : <span key={i}>{p}</span>));
}

// Topic tree — the reading structure of one video's extraction: each topic (with sector tags
// and its narrative) -> the companies it touches (stance-signed chips) -> its claims, each
// with a stance-colored left bar. Claims with no topic land in an "Other" tail section.
// ``hiRe`` (optional) highlights the entity/ticker that drove the opened driver's score.
const STANCE_BAR = { bullish: 'border-emerald-500', bearish: 'border-red-500' };
const STANCE_SIGN = { bullish: '+', bearish: '−' };

// Rail-3 per-claim SCOPE tag (market-wide vs company-specific) — for verifying the classifier.
// p = P(market-wide) ∈ [0,1]; null = not yet classified (hidden).
function ScopeTag({ p }) {
  if (p == null || typeof p !== 'number') return null;
  const label = p > 0.66 ? 'market-wide' : p < 0.33 ? 'company-specific' : 'unclear';
  const cls = p > 0.66 ? 'text-sky-700 border-sky-300 dark:text-sky-300 dark:border-sky-700'
    : p < 0.33 ? 'text-amber-700 border-amber-300 dark:text-amber-300 dark:border-amber-700'
    : 'text-gray-500 border-gray-300 dark:text-gray-400 dark:border-gray-600';
  return (
    <span className={`ml-1 inline-block rounded border px-1 py-px text-[9px] uppercase tracking-wide align-middle ${cls}`}
      title={`Scope: ${label} — P(market-wide) = ${p.toFixed(2)}. A market-wide claim reads across to peers; a company-specific one does not.`}>
      {label} {p.toFixed(2)}
    </span>
  );
}
function TopicTree({ topics, claims, hiRe }) {
  const byTopic = {};
  (claims || []).forEach((c) => {
    const k = c.topic_id || '_other';
    (byTopic[k] = byTopic[k] || []).push(c);
  });
  const list = [...(topics || [])];
  if (byTopic._other) list.push({ id: '_other', title: 'Other observations', sectors: [], summary: '', entities: [] });
  if (!list.length) return null;
  return (
    <div className="mt-3">
      <div className="mb-2 text-[10px] uppercase tracking-wider text-brand-gold">
        Topic tree — {list.length} topic{list.length > 1 ? 's' : ''} · each point → its companies (stance) → its claims
      </div>
      {list.map((t) => (
        <div key={t.id} className="mb-3 border-l-2 border-brand-gold/50 pl-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">{t.title}</span>
            {(t.sectors || []).map((s) => (
              <span key={s} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-800 dark:text-gray-400">{s}</span>
            ))}
          </div>
          {t.summary && <p className="mt-1 text-[12px] leading-snug text-gray-600 dark:text-gray-300">{t.summary}</p>}
          {(t.entities || []).length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {t.entities.map((e, i) => {
                const hit = hitsAny(e.name, hiRe);
                return (
                  <span key={i} className={`rounded border px-1.5 py-0.5 text-[10px] ${hit
                    ? 'border-brand-gold bg-brand-gold/15 text-gray-700 dark:text-gray-200'
                    : 'border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400'}`}>
                    <span className={e.stance === 'bullish' ? 'text-emerald-500' : e.stance === 'bearish' ? 'text-red-500' : 'text-gray-400'}>
                      {STANCE_SIGN[e.stance] || '−'}
                    </span>{' '}{e.name}
                  </span>
                );
              })}
            </div>
          )}
          {(byTopic[t.id] || []).map((c, i) => (
            <p key={i} className={`mt-1.5 border-l-2 pl-2 text-[12px] leading-snug text-gray-700 dark:text-gray-200 ${STANCE_BAR[c.direction] || 'border-gray-300 dark:border-gray-600'}`}
              title={c.quote ? `“${c.quote}”${c.speaker ? ` — ${c.speaker}` : ''}` : undefined}>
              <Highlighted text={c.claim} re={hiRe} /><ScopeTag p={c.scope} />
            </p>
          ))}
        </div>
      ))}
    </div>
  );
}

// Relationships view — the directed ties this video asserted (subject → relation → object),
// with the pair that drove the opened score highlighted. This is what actually routes a
// private company's narrative onto a tradeable ticker.
function RelationshipsView({ rels, hiRe }) {
  if (!rels || !rels.length) return <div className="mt-3 py-4 text-sm text-gray-400">No relationships extracted from this video.</div>;
  return (
    <div className="mt-3">
      <div className="mb-2 text-[10px] uppercase tracking-wider text-brand-gold">Relationships — subject → relation → object</div>
      {rels.map((r, i) => {
        const hit = hitsAny(r.subject, hiRe) || hitsAny(r.object, hiRe);
        return (
          <div key={i} className={`mb-1.5 rounded border-l-2 py-1 pl-2 text-[12px] ${hit
            ? 'border-brand-gold bg-brand-gold/10' : 'border-gray-200 dark:border-gray-700'}`}>
            <span className="text-gray-800 dark:text-gray-100"><Highlighted text={r.subject} re={hiRe} /></span>
            <span className={`mx-1 ${RELCLR[r.relation] || 'text-gray-400'}`}>{String(r.relation || '').replace(/_/g, ' ')}</span>
            <span className="text-gray-800 dark:text-gray-100"><Highlighted text={r.object} re={hiRe} /></span>
            {r.note && <span className="ml-1 text-gray-400">· {r.note}</span>}
          </div>
        );
      })}
    </div>
  );
}

// Full transcript view — timed lines when segments exist, else the raw block. Lines that
// mention the driving entity/ticker are highlighted and lightly tinted.
function TranscriptView({ transcript, hiRe }) {
  const segs = transcript?.segments || [];
  const fmtT = (s) => (s == null ? '' : `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`);
  if (!segs.length && transcript?.text) {
    return <p className="mt-3 whitespace-pre-wrap text-[12px] leading-relaxed text-gray-700 dark:text-gray-200"><Highlighted text={transcript.text} re={hiRe} /></p>;
  }
  if (!segs.length) return <div className="mt-3 py-4 text-sm text-gray-400">No transcript stored for this video.</div>;
  return (
    <div className="mt-3 space-y-0.5">
      {segs.map((s, i) => {
        const hit = hitsAny(s.text, hiRe);
        return (
          <div key={i} className={`flex gap-2 rounded px-1 text-[12px] leading-snug ${hit ? 'bg-brand-gold/10' : ''}`}>
            <span className="w-10 shrink-0 tabular-nums text-gray-400">{fmtT(s.start)}</span>
            <span className="text-gray-700 dark:text-gray-200"><Highlighted text={s.text} re={hiRe} /></span>
          </div>
        );
      })}
    </div>
  );
}

// User-facing extraction viewer — opens from a receipt link. Shows the investor-relevant
// extraction content (channel/title/date, thesis) in three views: Topic tree | Relationships
// | Transcript. Deliberately NOT the DeepSeek ops panel (no cost/token/grounding internals).
// ``highlight`` = {ticker, name, node, relation}: when a driver row opened this, the entity
// that DROVE the score is marked everywhere + a banner explains the routing (answers the
// "neutral in the summary but positive score" confusion — the score comes from the NEIGHBOUR).
export function ExtractionModal({ docId, highlight, onClose }) {   // also opened by the new Chatter feed
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [view, setView] = useState('tree');   // tree | rels | transcript
  useEffect(() => {
    let live = true;
    researchosService.getExtractionSummary(docId)
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setErr('Could not load the extraction.'); });
    return () => { live = false; };
  }, [docId]);
  const v = data?.video;
  const hiRe = useMemo(
    () => makeHiRe(highlight ? [highlight.ticker, highlight.name, highlight.node] : []),
    [highlight]);
  const seg = (val, setVal, opts) => (
    <span className="inline-flex overflow-hidden rounded border border-gray-200 dark:border-gray-700">
      {opts.map(([val2, lbl]) => (
        <button key={val2} onClick={() => setVal(val2)}
          className={`px-2 py-0.5 text-[10px] uppercase tracking-wide ${val === val2
            ? 'bg-brand-gold text-white'
            : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
          {lbl}
        </button>
      ))}
    </span>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-[8vh]" onClick={onClose}>
      <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-700 dark:bg-[#161b22]"
        onClick={(e) => e.stopPropagation()}>
        {err && <div className="text-sm text-red-500">{err}</div>}
        {!data && !err && <div className="text-sm text-gray-400">Loading extraction…</div>}
        {v && (
          <>
            <div className="mb-1 flex items-start justify-between gap-3">
              <div>
                <div className="font-serif text-base text-gray-900 dark:text-gray-100">{v.title || 'Untitled video'}</div>
                <div className="mt-0.5 text-[11px] uppercase tracking-wide text-gray-400">
                  {v.channel || 'unknown channel'}{v.published_at ? ` · ${v.published_at}` : ''}
                  {data.extracted_at ? ` · extracted ${data.extracted_at}` : ''}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <a href={v.url} target="_blank" rel="noreferrer" className="text-[11px] text-brand-gold hover:underline">watch ↗</a>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
              </div>
            </div>
            {highlight && (highlight.node || highlight.ticker) && (
              <div className="mt-2 rounded-lg border border-brand-gold/40 bg-brand-gold/10 p-2 text-[12px] leading-snug text-gray-700 dark:text-gray-200">
                This video's contribution to <b>{highlight.ticker}</b> is routed from{' '}
                <b>{highlight.node}</b>{highlight.relation ? <> ({String(highlight.relation).replace(/_/g, ' ')})</> : null} —
                so the score follows <b>{highlight.node}</b>’s sentiment here, not <b>{highlight.ticker}</b>’s own. Matches highlighted below.
              </div>
            )}
            {data.available === false && (
              <div className="py-4 text-sm text-gray-400">This video has a transcript but no committed extraction yet.</div>
            )}
            {data.thesis && (
              <p className="mt-2 rounded-lg bg-gray-50 p-3 text-[13px] leading-snug text-gray-700 dark:bg-gray-800/40 dark:text-gray-200">
                <Highlighted text={data.thesis} re={hiRe} />
              </p>
            )}
            <div className="mt-3">
              {seg(view, setView, [['tree', 'Topic tree'], ['rels', `Relationships${data.relationships?.length ? ` (${data.relationships.length})` : ''}`], ['transcript', 'Transcript']])}
            </div>
            {view === 'tree' && <TopicTree topics={data.topics} claims={data.claims} hiRe={hiRe} />}
            {view === 'rels' && <RelationshipsView rels={data.relationships} hiRe={hiRe} />}
            {view === 'transcript' && <TranscriptView transcript={data.transcript} hiRe={hiRe} />}
          </>
        )}
      </div>
    </div>
  );
}

function DriverRow({ d, newestFirst = false, onOpenDoc, subject }) {
  const evidence = d.evidence || [];
  // highlight context for the extraction viewer: the score's tradeable subject + the
  // private neighbour (d.from) that actually carried the sentiment through this edge.
  const hi = { ticker: subject?.ticker, name: subject?.name, node: d.from, relation: d.relation };
  // Stored artifacts carry sources in commit order — pick the 4 MOST RECENT videos.
  // In 'latest' mode show them newest-first (the fresh video leads); in 'impact'
  // mode show them chronologically (oldest → newest reading order).
  const recent = [...(d.sources || [])]
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
    .slice(0, 4);
  const sources = newestFirst ? recent : recent.reverse();
  return (
    <div className="border-b border-gray-100 py-2 last:border-0 dark:border-gray-800/60">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {evidence.length > 0
            ? evidence.slice(0, 2).map((ev, i) => (
                <p key={i} className="text-[13px] leading-snug text-gray-800 dark:text-gray-100">“{ev}”</p>
              ))
            : <p className="text-[13px] text-gray-500 dark:text-gray-400"><b>{d.from}</b> — {String(d.relation).replace(/_/g, ' ')}</p>}
          {sources.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              {sources.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[11px]">
                  <button
                    onClick={(e) => { e.stopPropagation(); if (s.doc_id && onOpenDoc) onOpenDoc(s.doc_id, hi); }}
                    className="text-brand-gold hover:underline"
                    title="Open the extraction summary">
                    ▶ {s.title || 'watch'}{s.date ? <span className="text-gray-400"> · {s.date}</span> : null}
                  </button>
                  {s.impact != null && (
                    <b className={`tabular-nums ${scoreColor(s.impact)}`}>{fmt(s.impact)}</b>
                  )}
                  <a href={s.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                    className="text-gray-400 hover:text-brand-gold" title="Watch on YouTube">↗</a>
                </span>
              ))}
            </div>
          )}
          <div className="mt-1 text-[10px] uppercase tracking-wide text-gray-400">
            {d.from} · <span className={RELCLR[d.relation] || ''}>{String(d.relation).replace(/_/g, ' ')}</span>
            {d.hop != null && <span className="ml-1 rounded bg-gray-100 px-1 py-px text-[9px] text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              {d.kind === 'competitor' ? 'competitor · 1 hop' : d.hop === 0 ? 'direct' : d.hop === 1 ? '1 hop' : `2 hops via ${d.via || '?'}`}
            </span>}
            {d.salience > 0 && <> · {d.salience} mention{d.salience > 1 ? 's' : ''}</>}
          </div>
        </div>
        <b className={`shrink-0 text-sm tabular-nums ${scoreColor(d.contribution)}`}>{fmt(d.contribution)}</b>
      </div>
    </div>
  );
}

// Video-level chronological breakdown for one expanded ticker: every (video, node) pair as its
// own row, newest video first — the same video legitimately appears once per node it activated,
// each with THAT edge's impact. This is the receipt stream in the order the narrative arrived.
function TimelineList({ drivers, onOpenDoc, subject }) {
  const [showAll, setShowAll] = useState(false);
  const rows = useMemo(() => {
    const out = [];
    (drivers || []).forEach((d) => (d.sources || []).forEach((s) => out.push({
      date: s.date || '', title: s.title || 'watch', url: s.url, doc_id: s.doc_id,
      impact: s.impact, node: d.from, relation: d.relation, text: s.text || '',
    })));
    out.sort((a, b) => b.date.localeCompare(a.date)
      || Math.abs(b.impact || 0) - Math.abs(a.impact || 0));
    return out;
  }, [drivers]);
  const shown = showAll ? rows : rows.slice(0, 40);
  return (
    <div>
      {shown.map((r, i) => (
        <div key={i} className="flex items-center gap-2 border-b border-gray-100 py-1 text-[12px] last:border-0 dark:border-gray-800/60">
          <span className="w-[74px] shrink-0 tabular-nums text-gray-400">{r.date || '—'}</span>
          <b className={`w-[58px] shrink-0 text-right tabular-nums ${scoreColor(r.impact)}`}>
            {r.impact != null ? fmt(r.impact) : '—'}
          </b>
          <span className="min-w-0 flex-1 truncate">
            {r.text && <span className="mr-1 text-gray-700 dark:text-gray-200" title={r.text}>“{r.text}”</span>}
            <button onClick={(e) => { e.stopPropagation(); if (r.doc_id && onOpenDoc) onOpenDoc(r.doc_id, { ticker: subject?.ticker, name: subject?.name, node: r.node, relation: r.relation }); }}
              className="text-brand-gold hover:underline" title="Open the extraction summary">
              ▶ {r.title}
            </button>
            <a href={r.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
              className="ml-1 text-gray-400 hover:text-brand-gold" title="Watch on YouTube">↗</a>
          </span>
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-gray-400">
            {r.node} · <span className={RELCLR[r.relation] || ''}>{String(r.relation).replace(/_/g, ' ')}</span>
          </span>
        </div>
      ))}
      {rows.length > shown.length && (
        <button onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
          className="mt-1.5 text-[11px] text-brand-gold hover:underline">
          Show all {rows.length} video rows
        </button>
      )}
    </div>
  );
}

// ── trajectory-chart math helpers (pure, on the VISIBLE slice) ──
const _mean = (a) => a.reduce((s, v) => s + v, 0) / (a.length || 1);
const _std = (a) => { const m = _mean(a); return Math.sqrt(_mean(a.map((v) => (v - m) ** 2))); };
// z-score a series over its own visible window (null-safe; flat series → zeros)
function zScore(vals) {
  const clean = vals.filter((v) => v != null && isFinite(v));
  if (clean.length < 2) return vals.map(() => null);
  const m = _mean(clean), s = _std(clean) || 1;
  return vals.map((v) => (v == null || !isFinite(v) ? null : (v - m) / s));
}
// week-over-week increments (first point has no prior → null)
const weeklyDelta = (vals) => vals.map((v, i) => (i === 0 || v == null || vals[i - 1] == null ? null : v - vals[i - 1]));
// rebase to 100 at the first non-null visible point (prices only — signals cross zero)
function rebase100(vals) {
  const base = vals.find((v) => v != null && isFinite(v) && v !== 0);
  return base == null ? vals.map(() => null) : vals.map((v) => (v == null ? null : (v / base) * 100));
}

const RANGES = [['3M', 13], ['6M', 26], ['1Y', 52], ['Max', 0]];          // label → weekly points (0 = all)
const DAILY_RANGES = [['3M', 63], ['6M', 126], ['1Y', 252], ['Max', 0]];  // label → daily points (0 = all)

// The Signal page shows ONE method — Rail-3 + novelty, the line built every day (owner 2026-10-19: the other methods —
// All / Independent / v2 / Rail-3 topic + claim — "were just for testing" and are suspended everywhere in the UI).
const LINE = 'rail3_novelty';
const LINE_TITLE = 'Rail-3 + novelty';

// The trajectory chart for one ticker of the main line: range pills, transform views (Level | Δ | Z-score),
// multi-ticker compare and price overlay (Z-both / dual axis).
function TrajectoryModal({ ticker, seriesMap, points, allTickers, traj, onClose }) {
  const daily = true;
  const [plotted, setPlotted] = useState([ticker]);
  const [pick, setPick] = useState('');
  const [range, setRange] = useState(daily ? 126 : 26);   // visible points; 0 = all
  const [view, setView] = useState('level');              // level | delta | z   (price overlay OFF)
  const [showPrice, setShowPrice] = useState(false);
  const [norm, setNorm] = useState('z');                  // z (z-both) | dual   (price overlay ON)
  const [priceMap, setPriceMap] = useState({});           // {ticker: [close per point]} — full-length
  const [priceErr, setPriceErr] = useState('');
  const theme = isDark() ? 'dark' : 'light';

  const addTicker = (t) => {
    const v = String(t || '').trim().toUpperCase();
    if (v && seriesMap[v] && !plotted.includes(v)) setPlotted((p) => [...p, v]);
    setPick('');
  };

  // closes once the overlay is on (and again if the plotted set grows); daily cadence for daily series
  useEffect(() => {
    if (!showPrice) return;
    const missing = plotted.filter((t) => !(t in priceMap));
    if (!missing.length) return;
    researchosService.getPropagationPrices(missing, 'daily')
      .then((r) => setPriceMap((m) => ({ ...m, ...(r.data?.prices || {}) })))
      .catch(() => setPriceErr('Could not load prices.'));
  }, [showPrice, plotted]);   // eslint-disable-line react-hooks/exhaustive-deps

  const n = points.length;
  const cut = range > 0 ? Math.max(0, n - range) : 0;
  const px = points.slice(cut);
  const sliceOf = (arr) => (arr || []).slice(cut);
  const xf = (y) => (showPrice ? (norm === 'z' ? zScore(y) : y) : view === 'z' ? zScore(y) : view === 'delta' ? weeklyDelta(y) : y);
  const isBar = !showPrice && view === 'delta';

  const traces = [];
  plotted.forEach((t) => {
    const sig = sliceOf(seriesMap[t]);
    traces.push({ x: px, y: xf(sig), type: isBar ? 'bar' : 'scatter', mode: isBar ? undefined : 'lines+markers',
      name: `${t} · ${LINE_TITLE}`, marker: { size: 4 }, line: { width: 2 } });
    if (showPrice) {
      const prc = sliceOf(priceMap[t] || []);
      if (norm === 'z') traces.push({ x: px, y: zScore(prc), type: 'scatter', mode: 'lines', name: `${t} price`, line: { width: 1.5, dash: 'dash' } });
      else traces.push({ x: px, y: rebase100(prc), type: 'scatter', mode: 'lines', name: `${t} price (=100)`, yaxis: 'y2', line: { width: 1.5, dash: 'dash' } });
    }
  });

  const unit = daily ? 'day' : 'week';
  const sub = showPrice
    ? (norm === 'z' ? 'signal vs price · both z-scored over the visible window' : 'signal (left) vs price rebased to 100 (right)')
    : view === 'delta' ? `${unit}-over-${unit} change in the score`
      : view === 'z' ? 'score standardized against its own visible history'
        : `${daily ? 'daily' : 'weekly'} score · ${daily ? `tradeable point-in-time${traj && traj.cutoff_hour_et ? ` (${traj.cutoff_hour_et} ET cutoff)` : ''}` : 'hover for values'}`;
  const seg = (val, setVal, opts) => (
    <span className="inline-flex overflow-hidden rounded border border-gray-200 dark:border-gray-700">
      {opts.map(([v, lbl]) => (
        <button key={v} onClick={() => setVal(v)}
          className={`px-2 py-0.5 text-[10px] uppercase tracking-wide ${val === v
            ? 'bg-brand-gold text-white'
            : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
          {lbl}
        </button>
      ))}
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-[6vh]" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-700 dark:bg-[#161b22]"
        onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center justify-between gap-3">
          <div>
            <div className="font-serif text-base text-gray-900 dark:text-gray-100">Trajectory — {LINE_TITLE}</div>
            <div className="text-[11px] uppercase tracking-wide text-gray-400">{sub}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">✕</button>
        </div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {seg(range, setRange, (daily ? DAILY_RANGES : RANGES).map(([lbl, w]) => [w, lbl]))}
          {!showPrice && seg(view, setView, [['level', 'Level'], ['delta', daily ? 'Δ day' : 'Δ wk'], ['z', 'Z-score']])}
          <label className="inline-flex cursor-pointer items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
            <input type="checkbox" checked={showPrice} onChange={(e) => setShowPrice(e.target.checked)} />
            Price overlay
          </label>
          {showPrice && seg(norm, setNorm, [['z', 'Z-both'], ['dual', 'Dual axis']])}
          {priceErr && <span className="text-[11px] text-red-500">{priceErr}</span>}
        </div>
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          {plotted.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[11px] dark:border-gray-700">
              {t}
              {plotted.length > 1 && (
                <button onClick={() => setPlotted((p) => p.filter((x) => x !== t))}
                  className="text-gray-400 hover:text-red-500">✕</button>
              )}
            </span>
          ))}
          <input list="traj-ticker-options" value={pick} placeholder="+ compare ticker"
            onChange={(e) => { setPick(e.target.value); if (seriesMap[e.target.value.trim().toUpperCase()]) addTicker(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') addTicker(pick); }}
            className="w-36 rounded border border-gray-200 bg-transparent px-2 py-0.5 text-[11px] text-gray-700 outline-none focus:border-brand-gold dark:border-gray-700 dark:text-gray-200" />
          <datalist id="traj-ticker-options">
            {allTickers.filter((t) => !plotted.includes(t)).map((t) => <option key={t} value={t} />)}
          </datalist>
        </div>
        <Plot data={traces}
          layout={mergeLayout({
            height: 400, margin: { l: 48, r: showPrice && norm === 'dual' ? 48 : 14, t: 8, b: 36 },
            showlegend: true, legend: { orientation: 'h', y: -0.18 },
            hovermode: 'x unified', barmode: 'group',
            ...(showPrice && norm === 'dual'
              ? { yaxis2: { overlaying: 'y', side: 'right', showgrid: false } } : {}),
          }, theme)}
          config={PLOTLY_CONFIG} style={{ width: '100%' }} useResizeHandler />
      </div>
    </div>
  );
}

const COLS = [
  { key: 'ticker', type: 'string', get: (r) => r.ticker },
  { key: 'score', type: 'number', get: (r) => r.score },
];

export default function PropagationPanel() {
  const [open, setOpen] = useState({});
  // 'timeline' = flat atom-level rows, newest first (DEFAULT); 'latest' = drivers ordered by their most recent
  // document; 'impact' = by |contribution|.
  const [driverSort, setDriverSort] = useState('timeline');
  const [docModal, setDocModal] = useState(null);   // {docId, highlight} for the extraction viewer
  const openDoc = (docId, highlight) => setDocModal({ docId, highlight });
  const [trajModal, setTrajModal] = useState(null); // {ticker} for the trajectory chart

  // The stored DAILY line (latest point = current value) and the stored drivers + receipts at that same point
  // (propagation_store; a page read never computes).
  const [traj, setTraj] = useState(null);
  useEffect(() => {
    researchosService.getPropagationTrajectories()
      .then((r) => setTraj(r.data)).catch(() => setTraj({ error: true }));
  }, []);
  const [attrib, setAttrib] = useState(null);        // {rows: SUMMARY per ticker, as_of, source} | {error}
  const [attribRows, setAttribRows] = useState({});  // {ticker: full row (drivers + receipts) | {error}}
  useEffect(() => {
    researchosService.getPropagationAttribution(LINE)
      .then((r) => setAttrib(r.data)).catch(() => setAttrib({ error: true, rows: {} }));
  }, []);
  const loadAttribRow = (ticker) => {               // on expand: the ticker's drivers + receipts (cached)
    if (attribRows[ticker]) return;
    setAttribRows((m) => ({ ...m, [ticker]: { loading: true } }));
    researchosService.getPropagationAttribution(LINE, ticker)
      .then((r) => setAttribRows((m) => ({ ...m, [ticker]: r.data.row || { empty: true } })))
      .catch(() => setAttribRows((m) => ({ ...m, [ticker]: { error: true } })));
  };
  const line = traj && !traj.error ? traj[LINE] : null;
  const points = (line && line.points) || [];
  const seriesMap = useMemo(() => {                // {ticker: daily scores}
    const m = {};
    ((line && line.series) || []).forEach((s) => { m[s.ticker] = s.scores; });
    return m;
  }, [line]);
  const ranked = useMemo(() => {
    const rows = (attrib && attrib.rows) || {};
    return Object.entries(seriesMap).map(([t, sc]) => {
      const a = rows[t] || {};
      const full = attribRows[t];
      return { key: t, ticker: t, name: (a.top && a.top.kind === 'direct' ? a.top.from : t),
        score: sc.length ? sc[sc.length - 1] : null, top: a.top || null,
        drivers: (full && full.drivers) || [], n_drivers: a.n_drivers, shown_sum: a.shown_sum };
    }).sort((a, b) => (b.score ?? -1e9) - (a.score ?? -1e9));
  }, [seriesMap, attrib, attribRows]);
  const { sorted, toggle, indicator } = useSortableRows(ranked, COLS, { key: 'score', dir: 'desc' });

  if (traj && traj.error) return <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">Failed to load the signal.</div>;
  if (!traj) return <div className="text-sm text-gray-500 dark:text-gray-400">Loading the signal…</div>;
  if (traj.building) return <div className="text-sm text-gray-500 dark:text-gray-400">{traj.note || 'Not built yet.'}</div>;

  const th = 'py-2 text-[11px] uppercase tracking-wider text-gray-500';
  const asOf = String((attrib && attrib.as_of) || points[points.length - 1] || '').slice(0, 10);
  const cutoff = traj.cutoff_hour_et != null ? `${Math.floor(traj.cutoff_hour_et)}:${String(Math.round((traj.cutoff_hour_et % 1) * 60)).padStart(2, '0')} ET` : '';

  return (
    <div>
      <div className="mb-3">
        <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100">Signal — {LINE_TITLE}</h2>
        <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
          Each company's own sentiment plus what flows in from one and two relationships away. A <b>candidate
          watchlist</b> — deterministic, with drivers. Not a trade signal.
        </p>
        <div className="mt-2 text-[11px] uppercase tracking-wider text-gray-400">
          {ranked.length} tickers · as-of {asOf}{cutoff ? ` · ${cutoff} cutoff` : ''}{attrib == null ? ' · loading drivers…' : ''}
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800">
              <SortableTh label="Ticker" colKey="ticker" toggle={toggle} indicator={indicator} align="left" className={`${th} pr-3`} />
              <SortableTh label="Signal" colKey="score" toggle={toggle} indicator={indicator} align="right" className={`${th} pr-3`} />
              <th className={`${th} pr-3 text-left`}>63-day trajectory</th>
              <th className={`${th} text-right`}>Top driver</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const isOpen = !!open[r.ticker];
              const top = r.top;
              const full = attribRows[r.ticker];
              return (
                <React.Fragment key={r.key}>
                  <tr onClick={() => { if (!open[r.ticker]) loadAttribRow(r.ticker); setOpen((o) => ({ ...o, [r.ticker]: !o[r.ticker] })); }}
                    className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-gray-800/40">
                    <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">
                      <span className="mr-1 inline-block w-3 text-gray-400">{isOpen ? '▾' : '▸'}</span>{r.ticker}
                    </td>
                    <td className={`py-2 pr-3 text-right font-semibold tabular-nums ${scoreColor(r.score)}`}>{fmt(r.score)}</td>
                    <td className="py-2 pr-3">
                      <button onClick={(e) => { e.stopPropagation(); setTrajModal({ ticker: r.ticker }); }}
                        title="Open the trajectory chart (values · compare · price)"
                        className="cursor-zoom-in rounded px-0.5 hover:bg-gray-100 dark:hover:bg-gray-800">
                        <Spark scores={(seriesMap[r.ticker] || []).slice(-63)} />
                      </button>
                    </td>
                    <td className="py-2 text-right text-xs text-gray-500 dark:text-gray-400">
                      {top ? <span className="truncate">{top.from} <span className={RELCLR[top.relation] || 'text-gray-400'}>{String(top.relation).replace(/_/g, ' ')}</span>{top.hop === 2 ? <span className="text-gray-400"> via {top.via}</span> : null}</span> : '—'}
                      <span className="ml-2 text-gray-300 dark:text-gray-600">{r.n_drivers || 0}</span>
                    </td>
                  </tr>
                  {isOpen && !(r.drivers && r.drivers.length) && (
                    <tr className="bg-gray-50/60 dark:bg-gray-800/20">
                      <td colSpan={4} className="px-6 py-2 text-xs text-gray-500 dark:text-gray-400">
                        {attrib && attrib.error ? 'Could not load the receipts.'
                          : attrib && attrib.building ? 'The receipts are not built yet — they are built with the next daily line.'
                            : full && full.error ? 'Could not load this ticker\'s receipts.'
                              : full && full.empty ? 'No drivers at the latest point for this ticker.'
                                : 'Loading receipts…'}
                      </td>
                    </tr>
                  )}
                  {isOpen && r.drivers && r.drivers.length > 0 && (
                    <tr className="bg-gray-50/60 dark:bg-gray-800/20">
                      <td colSpan={4} className="px-6 py-2">
                        <div className="mb-1 flex items-center justify-between">
                          <div className="text-[10px] uppercase tracking-wider text-gray-400">
                            What was said — and where
                            <span className="ml-2 rounded bg-brand-gold/15 px-1.5 py-0.5 text-brand-gold">
                              ex-official · as-of {String((attrib && attrib.as_of) || '').slice(0, 10)}
                              {r.n_drivers > r.drivers.length ? ` · top ${r.drivers.length} of ${r.n_drivers} drivers (shown ${fmt(r.shown_sum)} of ${fmt(r.score)})` : ''}
                            </span>
                          </div>
                          <div className="flex overflow-hidden rounded border border-gray-200 text-[10px] uppercase tracking-wide dark:border-gray-700">
                            {[['timeline', 'Timeline'], ['latest', 'Latest'], ['impact', 'Impact']].map(([v, lbl]) => (
                              <button key={v} onClick={(e) => { e.stopPropagation(); setDriverSort(v); }}
                                className={`px-2 py-0.5 ${driverSort === v
                                  ? 'bg-brand-gold text-white'
                                  : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                                {lbl}
                              </button>
                            ))}
                          </div>
                        </div>
                        {driverSort === 'timeline'
                          ? <TimelineList drivers={r.drivers} onOpenDoc={openDoc} subject={{ ticker: r.ticker, name: r.name }} />
                          : sortDrivers(r.drivers, driverSort).map((d, i) => <DriverRow key={i} d={d} newestFirst={driverSort === 'latest'} onOpenDoc={openDoc} subject={{ ticker: r.ticker, name: r.name }} />)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11px] text-gray-400">
        {LINE_TITLE} = the company's own claims (echoes down-weighted, official channels excluded) + what flows in from one
        and two relationships away (halved per hop), tie weights by materiality, rivals signed by market-wide vs share-taking.
        Expand any row for its drivers and receipts (they sum to the score). Trajectory = the daily score at the {cutoff || 'daily'} cutoff.
      </p>
      {docModal != null && <ExtractionModal docId={docModal.docId} highlight={docModal.highlight} onClose={() => setDocModal(null)} />}
      {trajModal != null && (
        <TrajectoryModal ticker={trajModal.ticker} seriesMap={seriesMap} points={points}
          allTickers={Object.keys(seriesMap).sort()} traj={traj} onClose={() => setTrajModal(null)} />
      )}
    </div>
  );
}
